import React, { useEffect, useMemo, useState } from 'react';
import { Classes, Dialog, InputGroup, NonIdealState, Spinner } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';

// Written into the build by scripts/generate-third-party-licenses.js.
const LICENSES_URL = `${process.env.PUBLIC_URL}/third-party-licenses.json`;

interface ThirdPartyPackage {
  name: string;
  version: string;
  license: string;
  repository: string;
  licenseText: string;
  noticeText?: string;
  bundledIn?: string;
}

interface ThirdPartyLicenses {
  generated: string;
  packages: ThirdPartyPackage[];
  bundledNotices: string;
}

interface ThirdPartyLicensesDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ThirdPartyLicensesDialog: React.FC<ThirdPartyLicensesDialogProps> = ({ isOpen, onClose }) => {
  const [licenses, setLicenses] = useState<ThirdPartyLicenses | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [filter, setFilter] = useState('');

  // Fetched on first open only; the list is a few hundred kilobytes.
  useEffect(() => {
    if (!isOpen || licenses || loadFailed) {
      return;
    }

    fetch(LICENSES_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json() as Promise<ThirdPartyLicenses>;
      })
      .then(setLicenses)
      .catch(() => setLoadFailed(true));
  }, [isOpen, licenses, loadFailed]);

  const packages = useMemo(() => {
    const query = filter.trim().toLowerCase();
    return (licenses?.packages ?? []).filter((item) => (
      !query || item.name.toLowerCase().includes(query) || item.license.toLowerCase().includes(query)
    ));
  }, [licenses, filter]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Third-party licenses"
      icon={IconNames.Document}
      className="cm-licenses-dialog"
      canOutsideClickClose={true}
      canEscapeKeyClose={true}
    >
      <div className={`${Classes.DIALOG_BODY} cm-licenses-body`}>
        <p>
          CppModel Workspace includes the open source software listed below. Each component is
          used under the license shown with it.
        </p>

        {!licenses && !loadFailed && <Spinner size={24} />}

        {loadFailed && (
          <NonIdealState
            icon={IconNames.Document}
            title="License notices unavailable"
            description="They are generated by the production build: run npm run build and serve the build folder."
          />
        )}

        {licenses && (
          <>
            <InputGroup
              leftIcon={IconNames.Search}
              placeholder="Filter by package or license"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <p className="cm-licenses-count">
              {packages.length} of {licenses.packages.length} components
            </p>
            <ul className="cm-licenses-list">
              {packages.map((item) => (
                <li key={`${item.name}@${item.version}`}>
                  <details>
                    <summary>
                      <span className="cm-licenses-name">{item.name}{item.version && ` ${item.version}`}</span>
                      <span className="cm-licenses-type">{item.license}</span>
                    </summary>
                    {item.bundledIn && <p className="cm-licenses-meta">Bundled inside {item.bundledIn}.</p>}
                    {item.repository && (
                      <p className="cm-licenses-meta">
                        <a href={item.repository} target="_blank" rel="noreferrer">{item.repository}</a>
                      </p>
                    )}
                    {item.licenseText
                      ? <pre className="cm-licenses-text">{item.licenseText}</pre>
                      : (
                        <p className="cm-licenses-meta">
                          {item.license === 'UNKNOWN'
                            ? 'License details are not available in this build.'
                            : `Licensed under ${item.license}. The package does not include its license text.`}
                        </p>
                      )}
                    {item.noticeText && <pre className="cm-licenses-text">{item.noticeText}</pre>}
                  </details>
                </li>
              ))}
            </ul>

            {licenses.bundledNotices && (
              <details className="cm-licenses-notices">
                <summary>Notices preserved in the bundled code</summary>
                <pre className="cm-licenses-text">{licenses.bundledNotices}</pre>
              </details>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
};
